import { AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';

import { BillingSubscriptionStatus } from '@/__generated__/graphql';
import { useT } from '@/core/providers/I18nProvider';
import { useBillingTier } from '@/hooks/useBillingTier';

import type { ReactElement } from 'react';

/**
 * Global payment-failed banner (C27 leg 3). When the workspace subscription is
 * `past_due` — a renewal charge failed — this surfaces a persistent alert across
 * every dashboard page directing the owner to update their payment method.
 *
 * Syncanix does NOT send its own dunning emails: Paddle is the Merchant of
 * Record and runs the full retry + payment-reminder email sequence natively
 * (the C27 leg-3 decision — duplicate emails would only confuse). This
 * in-product banner is the dashboard half of that: the owner sees the failure
 * the moment they open the app, and the CTA routes to Settings → Billing where
 * "Manage billing" opens the Paddle customer portal to fix the card.
 *
 * Hidden on every other status (and while the tier read is unresolved — an
 * unknown status must never flash a payment warning at a healthy account). RTL-
 * correct via logical spacing; `role="alert"` announces it once on appearance.
 */
export function PaymentAlertBanner(): ReactElement | null {
  const { t } = useT();
  const { status } = useBillingTier();

  if (status !== BillingSubscriptionStatus.PastDue) {
    return null;
  }

  return (
    <div
      role="alert"
      className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2 border-b border-danger/40 bg-danger/10 px-6 py-3 text-sm text-danger-foreground"
    >
      <AlertTriangle aria-hidden="true" className="h-4 w-4 shrink-0" />
      <div className="flex-1">
        <span className="font-medium">{t('billing.paymentAlert.title')}</span>{' '}
        <span>{t('billing.paymentAlert.body')}</span>
      </div>
      <Link
        to="/settings/billing"
        className="shrink-0 rounded-md bg-destructive px-3 py-1.5 font-medium text-destructive-foreground underline-offset-2 hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      >
        {t('billing.paymentAlert.action')}
      </Link>
    </div>
  );
}
